import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from "../../components/ui/input";
import Checkbox from "antd/es/checkbox/Checkbox";
import { Save, ChevronDown, ChevronUp, ChevronLeftIcon } from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import { USERPERMISSION } from '../../constants/api';
import ToastMsg from '../../components/common/ToastMsg';
import { ContainerCard } from '../../components/ui/cards';

const UserPermissionEdit = () => {
    const { fetch, update } = useApiMethods();
    const navigate = useNavigate();
    const location = useLocation();
    const { state } = location;

    const [loading, setLoading] = useState(false);
    const [expandedSections, setExpandedSections] = useState(new Set());
    const [formData, setFormData] = useState({
        id: null,
        userGroup_name: "",
        userGroup_id: null,
        notes: "",
        permissions: []
    });

    const customConfig = JSON.parse(sessionStorage.getItem("customConfig") || "{}");
    const isNabhEnabled = !!customConfig.is_nabh;

    // Permission structure that matches your API response
    const permissionStructure = [
        { key: 'home', label: 'Home', type: 'single' },
        {
            key: 'administrator',
            label: 'Admin',
            type: 'group',
            subItems: [
                { key: 'organization', label: 'Organization' },
                { key: 'user_group', label: 'User Group' },
                { key: 'user_permission', label: 'User Permission' },
                { key: 'notice_board', label: 'Notice Board' },
                { key: 'password_policy', label: 'Password Policy' },
                { key: 'organization_documents', label: 'Organization Documents' }
            ]
        },
        {
            key: 'master',
            label: 'Master',
            type: 'group',
            subItems: [
                { key: 'employee_type', label: 'Employee Type' },
                { key: 'department', label: 'Department' },
                { key: 'designation', label: 'Designation' },
                { key: 'leave_type', label: 'Leave Type' },
                { key: 'employee_group', label: 'Employee Group' },
                { key: 'reimbursements_type', label: 'Reimbursements Type' },
                { key: 'asset_type', label: 'Asset Type' },
                { key: 'asset_category', label: 'Asset Category' },
                { key: 'asset_sub_category', label: 'Asset Sub Category' },
                { key: 'letter_type', label: 'Letter Type' },
                { key: 'letter_template', label: 'Letter Template' }
            ]
        },
        {
            key: 'configuration',
            label: 'Configuration',
            type: 'group',
            subItems: [
                { key: 'shift_configuration', label: 'Shift Configuration' },
                { key: 'holiday', label: 'Holiday' },
                { key: 'week_off', label: 'Week off' },
                { key: 'payroll_period', label: 'Payroll Period' },
                { key: 'subordinate', label: 'Subordinate' },
                { key: 'custom_configuration', label: 'Custom Configuration' },
                { key: 'salary_component', label: 'Salary Component' },
                { key: 'salary_template', label: 'Salary Template' }
            ]
        },
        {
            key: 'employee',
            label: 'Employee',
            type: 'group',
            subItems: [
                { key: 'employees', label: 'Employee List' },
                { key: 'approval_inbox', label: 'Approval Inbox' },
                { key: 'team_timesheet', label: 'Team Timesheet' },

                // { key: 'tds_declaration', label: 'TDS Declaration' },
                // { key: 'employee_onboarding', label: 'Employee Onboarding' },
                // { key: 'intern_onboarding', label: 'Intern Onboarding' }
            ]
        },
        {
            key: 'payroll',
            label: 'Payroll',
            type: 'group',
            subItems: [
                { key: 'run_payroll', label: 'Run Payroll' },
                { key: 'payroll', label: 'Payroll' },
                { key: 'payslips', label: 'Payslips' },
                // TDS Preview shows every employee's salary and tax projection;
                // TDS Configuration sets the statutory rates for the company.
                // Separate keys because they suit very different audiences.
                { key: 'tds_preview', label: 'TDS Preview' },
                { key: 'tds_configuration', label: 'TDS Configuration' }
            ]
        },
        {
            key: 'asset_management',
            label: 'Asset Management',
            type: 'group',
            subItems: [
                { key: 'asset_request', label: 'Asset Request' },
                { key: 'asset_registry', label: 'Asset Registry' },
                { key: 'asset_employee_audit', label: 'Asset Employee Audit' }
            ]
        },
        {
            key: 'reports',
            label: 'Reports',
            type: 'group',
            subItems: [
                { key: 'day_wise_attendance', label: 'Day wise Attendance' },
                { key: 'month_wise_attendance', label: 'Month Wise Attendance' },
                { key: 'employee_esi_statement', label: 'Employee ESI Statement' },
                { key: 'employee_pf_statement', label: 'Employee PF Statement' },
                { key: 'salary_process_bank', label: 'Salary Process Bank' },
                { key: 'payroll_detailed_summary', label: 'Payroll Detailed Summary' },
                { key: 'employee_earning_deduction', label: 'Employee Earning Deduction' },
                { key: 'muster_roll', label: 'Muster Roll' },
                { key: 'subordinates', label: 'Subordinates' },
                { key: 'employee_salary_summary', label: 'Employee Salary Summary' },
                { key: 'attendance_correction', label: 'Attendance Correction' },
                { key: 'roster_report', label: 'Roster Report' },
                { key: 'leave_permission_report', label: 'Leave Permission Report' },
                { key: 'short_duty_report', label: 'Short-Duty Review' }
            ]
        },
        ...(isNabhEnabled ? [{
            key: 'training',
            label: 'Training',
            type: 'group',
            subItems: [
                { key: 'my_trainings', label: 'My Trainings' },
                { key: 'team_trainings', label: 'Team Trainings' },
                { key: 'assign_training', label: 'Assign Training' }
            ]
        }] : []),
        { key: 'roster', label: 'Roster', type: 'single' }
    ];

    // Initialize permissions
    const initializePermissions = (apiPermissions = []) => {
        const defaultPermissions = permissionStructure.map(section => {
            if (section.type === 'single') {
                const apiModule = apiPermissions.find(p => p.module_name === section.key);
                return {
                    module_name: section.key,
                    module_list: {
                        [section.key]: apiModule?.module_list?.[section.key] || {
                            permission: false,
                            active: false,
                            edit: false,
                            add: false,
                            draft: false
                        }
                    }
                };
            } else {
                const apiModule = apiPermissions.find(p => p.module_name === section.key);
                const moduleList = {};

                section.subItems.forEach(subItem => {
                    moduleList[subItem.key] = apiModule?.module_list?.[subItem.key] || {
                        permission: false,
                        active: false,
                        edit: false,
                        add: false,
                        draft: false
                    };
                });

                return {
                    module_name: section.key,
                    module_list: moduleList
                };
            }
        });

        return defaultPermissions;
    };

    const getPermissionData = useCallback(async () => {
        try {
            setLoading(true);
            const res = await fetch(USERPERMISSION, { id: state?.usergroup_id });

            if (res?.data?.result?.[0]) {
                const data = res.data.result[0];
                const initializedPermissions = initializePermissions(data.permissions);

                setFormData({
                    id: data.id,
                    userGroup_name: data.usergroup?.user_group_name || "",
                    userGroup_id: data.usergroup?.usergroup_id || null,
                    notes: data.notes || "",
                    permissions: initializedPermissions
                });
            }
        } catch (error) {
            console.error('Error fetching permission data:', error);
            ToastMsg('error', 'Failed to load permission data');
        } finally {
            setLoading(false);
        }
    }, [state?.usergroup_id]);

    useEffect(() => {
        if (state?.usergroup_id) {
            getPermissionData();
        } else {
            setFormData(prev => ({
                ...prev,
                permissions: initializePermissions()
            }));
        }
    }, [state?.usergroup_id, getPermissionData]);

    const getPermissionValue = (moduleName, permissionType, subItemKey = null) => {
        const module = formData.permissions.find(p => p.module_name === moduleName);
        if (!module) return false;

        if (subItemKey) {
            return module.module_list[subItemKey]?.[permissionType] || false;
        }

        const singleItem = module.module_list[moduleName];
        return singleItem?.[permissionType] || false;
    };

    const updatePermission = (moduleName, permissionType, value, subItemKey = null) => {
        setFormData(prev => {
            const newPermissions = prev.permissions.map(module => {
                if (module.module_name === moduleName) {
                    const updatedModule = { ...module };

                    if (subItemKey) {
                        updatedModule.module_list = {
                            ...updatedModule.module_list,
                            [subItemKey]: {
                                ...updatedModule.module_list[subItemKey],
                                [permissionType]: value
                            }
                        };
                    } else {
                        updatedModule.module_list = {
                            ...updatedModule.module_list,
                            [moduleName]: {
                                ...updatedModule.module_list[moduleName],
                                [permissionType]: value
                            }
                        };
                    }

                    return updatedModule;
                }
                return module;
            });

            return { ...prev, permissions: newPermissions };
        });
    };

    const toggleSection = (key) => {
        setExpandedSections(prev => {
            const newSet = new Set(prev);
            if (newSet.has(key)) {
                newSet.delete(key);
            } else {
                newSet.add(key);
            }
            return newSet;
        });
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            const payload = {
                id: formData.id,
                notes: formData.notes,
                permissions: formData.permissions
            };

            const res = await update(USERPERMISSION, payload);

            if (res.status === "success") {
                ToastMsg("success", res.message);
                navigate('/admin/userpermission');
            } else {
                ToastMsg("error", res.message || "Failed to update permissions");
            }
        } catch (error) {
            console.error('Error updating permissions:', error);
            ToastMsg('error', 'Failed to update permissions');
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigate('/admin/userpermission');
    };

    if (loading && !formData.id) {
        return (
            <div className="p-6 dark:bg-gray-900 min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sidebar mx-auto"></div>
                    <p className="mt-4 text-gray-600 dark:text-gray-400">Loading permissions...</p>
                </div>
            </div>
        );
    }

    return (
        <ContainerCard>
            {/* Header */}
            <div className="flex items-center gap-4 mb-2">
                <ChevronLeftIcon className=" cursor-pointer" onClick={handleBack} />
                <div>
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                        Edit User Permissions
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Configure permissions for {formData.userGroup_name}
                    </p>
                </div>
            </div>

            {/* Main Content */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border shadow-sm p-5">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            User Group
                        </label>
                        <Input
                            value={formData.userGroup_name}
                            readOnly
                            className="w-full bg-gray-50 dark:bg-gray-700"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Notes
                        </label>
                        <Input
                            value={formData.notes}
                            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                            placeholder="Add notes..."
                            className="w-full"
                        />
                    </div>
                </div>

                {/* Permissions Header */}
                <div className="grid grid-cols-11 gap-2 mb-4 px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-lg font-medium text-sm">
                    <div className="col-span-5 text-gray-700 dark:text-gray-300">
                        Modules
                    </div>
                    {['Permission', 'Active', 'Edit', 'Add', 'Draft'].map((label, index) => (
                        <div
                            key={label}
                            className={`col-span-1 text-center text-gray-700 dark:text-gray-300 ${index > 0 ? 'pl-2' : ''}`}
                        >
                            {label}
                        </div>
                    ))}
                    <div className="col-span-1"></div>
                </div>

                {/* Permissions List */}
                <div className="space-y-1">
                    {permissionStructure.map((section) => (
                        <div key={section.key} className="border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                            {section.type === 'single' ? (
                                // Single permission item
                                <div className="grid grid-cols-11 gap-2 items-center py-3 px-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded">
                                    <div className="col-span-5">
                                        <span className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                            {section.label}
                                        </span>
                                    </div>
                                    {['permission', 'active', 'edit', 'add', 'draft'].map((permType, index) => (
                                        <div
                                            key={permType}
                                            className={`col-span-1 flex justify-center ${index > 0 ? 'pl-2' : ''}`}
                                        >
                                            <Checkbox
                                                checked={getPermissionValue(section.key, permType)}
                                                onChange={(e) => updatePermission(section.key, permType, e.target.checked)}
                                            />
                                        </div>
                                    ))}
                                    <div className="col-span-1"></div>
                                </div>
                            ) : (
                                // Grouped permissions with expandable section
                                <>
                                    <div
                                        className="grid grid-cols-11 gap-2 items-center py-3 px-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded"
                                        onClick={() => toggleSection(section.key)}
                                    >
                                        <div className="col-span-5">
                                            <span className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                                {section.label}
                                            </span>
                                        </div>
                                        {['permission', 'active', 'edit', 'add', 'draft'].map((permType, index) => (
                                            <div
                                                key={permType}
                                                className={`col-span-1 flex justify-center ${index > 0 ? 'pl-2' : ''}`}
                                            >
                                                <Checkbox
                                                    checked={section.subItems.every(subItem =>
                                                        getPermissionValue(section.key, permType, subItem.key)
                                                    )}
                                                    onChange={(e) => {
                                                        section.subItems.forEach(subItem => {
                                                            updatePermission(section.key, permType, e.target.checked, subItem.key);
                                                        });
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </div>
                                        ))}
                                        <div className="col-span-1 flex justify-center">
                                            {expandedSections.has(section.key) ? (
                                                <ChevronUp className="w-4 h-4" />
                                            ) : (
                                                <ChevronDown className="w-4 h-4" />
                                            )}
                                        </div>
                                    </div>

                                    {expandedSections.has(section.key) && (
                                        <div className="bg-gray-50 dark:bg-gray-700/30">
                                            {section.subItems.map((subItem) => (
                                                <div
                                                    key={subItem.key}
                                                    className="grid grid-cols-11 gap-2 items-center py-2 px-4 border-t border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600/50"
                                                >
                                                    <div className="col-span-5 pl-6">
                                                        <span className="text-sm text-gray-700 dark:text-gray-300">
                                                            {subItem.label}
                                                        </span>
                                                    </div>
                                                    {['permission', 'active', 'edit', 'add', 'draft'].map((permType, index) => (
                                                        <div
                                                            key={permType}
                                                            className={`col-span-1 flex justify-center ${index > 0 ? 'pl-2' : ''}`}
                                                        >
                                                            <Checkbox
                                                                checked={getPermissionValue(section.key, permType, subItem.key)}
                                                                onChange={(e) => updatePermission(section.key, permType, e.target.checked, subItem.key)}
                                                            />
                                                        </div>
                                                    ))}
                                                    <div className="col-span-1"></div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    ))}
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
                    <Button
                        variant="outline"
                        onClick={handleBack}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="bg-sidebar hover:bg-sidebar-primary dark:bg-blue-600 dark:hover:bg-blue-700 text-white"
                    >
                        {loading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        ) : (
                            <Save className="w-4 h-4 mr-2" />
                        )}
                        Update Permissions
                    </Button>
                </div>
            </div>
        </ContainerCard>
    );
};

export default UserPermissionEdit;